import { useState } from 'react';
import {
  Avatar,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  useAuth_ImpersonateMutation,
  User_ClientFieldsFragment,
  useUser_GetCurrentUserClientsQuery,
} from 'queries';
import { MoreVertical } from 'ts-react-feather-icons';

import Spinner from 'common/spinner';
import DitStatusLabel from 'pages/common/client/dit-status-label';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

const useStyles = makeStyles((theme) => ({
  taskContainer: {
    marginTop: '12px',
    paddingTop: '15px',
    width: '100%',
    background: '#FFFFFF',
    justifyContent: 'center',
    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.25)',
    borderRadius: '2px',
    height: '300px',
    overflow: 'auto',
  },
  title: {
    fontWeight: 600,
    marginTop: '5vh',
  },

  list: {
    overflow: 'auto',
    height: '350',
  },
  listItem: {
    border: '2px solid #F0F3F6',
    width: '95%',
    margin: 'auto',
    boxSizing: 'border-box',
    marginBottom: '-2px',
  },
  listTagContainer: {
    alignSelf: 'center',
    [theme.breakpoints.up('lg')]: {
      display: 'flex',
      marginLeft: '-5vw',
    },
  },
  listTag: {
    border: '1px solid #DAE4ED',
    boxSizing: 'border-box',
    backgroundColor: '#FFFCF0',
    minWidth: '63px',
    textAlign: 'center',
    alignItems: 'center',
    float: 'left',
    height: '29px',
    justifyContent: 'center',
    display: 'flex',
    margin: '1px',
  },
  taskTitle: { display: 'flex' },
  listAction: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  listItemText: {
    display: 'flex',
  },
}));

export default function MyClients() {
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedClient, setSelectedClient] = useState<User_ClientFieldsFragment>();
  const [impersonate] = useAuth_ImpersonateMutation({
    onCompleted: () => {
      // react history.push() doesn't work because a page reload is needed for impersonate to take effect
      // TODO: Remove the need of a page refresh for impersonate to take effect.
      window.location.href = `/home/${selectedClient!.defaultPortal.id}/home`;
    },
    onError: (e) => {
      showAlert({
        message: 'Error impersonating user: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });
  const { data: clientsData, loading: clientsLoading } = useUser_GetCurrentUserClientsQuery({
    onError: (e) => {
      showAlert({
        message: 'Error getting current user clients: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClick = () => {
    setAnchorEl(null);
  };

  const handleImpersonation = async () => {
    await impersonate({
      variables: { userId: selectedClient!.id },
    });
  };

  return (
    <>
      {clientsData && !clientsLoading ? (
        <>
          <Typography className={classes.title} variant="h5">
            My Clients
          </Typography>
          <div className={classes.taskContainer}>
            <div>
              <List className={classes.list}>
                {clientsData.data.map((client) => {
                  return (
                    <ListItem
                      key={client.id}
                      button
                      className={classes.listItem}
                      disableRipple
                      id="my-client-list-item"
                    >
                      <Grid container>
                        <Grid item xl={11} xs={9} className={classes.taskTitle}>
                          <ListItemIcon>
                            <Avatar
                              alt="Profile Photo"
                              src={client.avatarUrl || '/default_avatar.svg'}
                            />
                          </ListItemIcon>
                          <ListItemText primary={`${client.firstName} ${client.lastName}`} />
                        </Grid>
                        <Grid item xl={1} xs={3} className={classes.listTagContainer}>
                          <DitStatusLabel
                            efxDigitalIdentityTrust={client?.efxDigitalIdentityTrust || undefined}
                          />
                        </Grid>
                      </Grid>
                      <ListItemSecondaryAction className={classes.listAction}>
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            setSelectedClient(client);
                            handleClick(e);
                          }}
                          aria-controls="simple-menu"
                          aria-haspopup="true"
                          size="large"
                        >
                          <MoreVertical />
                        </IconButton>
                      </ListItemSecondaryAction>
                      <Menu
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        keepMounted
                        onClose={() => handleMenuClick()}
                        id="my-client-action-button"
                      >
                        <MenuItem
                          key="impersonate"
                          onClick={() => {
                            handleMenuClick();
                            handleImpersonation();
                          }}
                          id="my-client-impersonate-button"
                        >
                          impersonate
                        </MenuItem>
                      </Menu>
                    </ListItem>
                  );
                })}
              </List>
            </div>
          </div>
        </>
      ) : (
        <Spinner />
      )}
    </>
  );
}
